import { useState } from "react";
import { useForm } from "react-hook-form";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const WEB3FORMS_URL = "https://api.web3forms.com/submit";
const ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || "";

export const ContactForm = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  // status : idle | sending | success | error
  const [status, setStatus] = useState("idle");
  const [feedback, setFeedback] = useState("");

  const onSubmit = async (data) => {
    setStatus("sending");
    setFeedback("");

    if (!ACCESS_KEY || ACCESS_KEY === "YOUR_WEB3FORMS_ACCESS_KEY_HERE") {
      setStatus("error");
      setFeedback(
        "Le formulaire n'est pas encore configuré. Merci de nous appeler directement en attendant."
      );
      return;
    }

    try {
      const payload = {
        access_key: ACCESS_KEY,
        subject: "Nouveau message — Au Fournil de Pierre",
        from_name: data.name,
        name: data.name,
        email: data.email,
        message: data.message,
        botcheck: data.botcheck || "",
      };

      const res = await fetch(WEB3FORMS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const json = await res.json();

      if (json.success) {
        setStatus("success");
        setFeedback("Merci ! Votre message est bien parti. Nous vous répondrons rapidement.");
        reset();
      } else {
        setStatus("error");
        setFeedback(
          json.message || "L'envoi a échoué. Merci de réessayer ou de nous appeler."
        );
      }
    } catch (e) {
      setStatus("error");
      setFeedback(
        "Une erreur réseau est survenue. Merci de réessayer ou de nous appeler."
      );
    }
  };

  const fieldClass =
    "w-full bg-transparent border-b border-crustborder py-3 text-charcoal placeholder:text-ash/60 focus:border-crust transition-colors duration-300 outline-none";

  return (
    <form
      data-testid="contact-form"
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-7"
      noValidate
    >
      {/* Honeypot anti-spam — caché */}
      <input
        type="checkbox"
        tabIndex={-1}
        autoComplete="off"
        {...register("botcheck")}
        style={{ position: "absolute", left: "-9999px", opacity: 0 }}
        aria-hidden="true"
      />

      <div>
        <label htmlFor="name" className="kicker text-ash block mb-2">
          Votre nom
        </label>
        <input
          id="name"
          type="text"
          data-testid="contact-name"
          placeholder="Marie Dupont"
          className={fieldClass}
          {...register("name", { required: "Merci d'indiquer votre nom." })}
        />
        {errors.name && (
          <p data-testid="error-name" className="text-destructive text-xs mt-2">
            {errors.name.message}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="email" className="kicker text-ash block mb-2">
          Votre e-mail
        </label>
        <input
          id="email"
          type="email"
          data-testid="contact-email"
          placeholder="marie@exemple.fr"
          className={fieldClass}
          {...register("email", {
            required: "Merci d'indiquer votre e-mail.",
            pattern: {
              value: /^\S+@\S+\.\S+$/,
              message: "Cette adresse e-mail ne semble pas valide.",
            },
          })}
        />
        {errors.email && (
          <p data-testid="error-email" className="text-destructive text-xs mt-2">
            {errors.email.message}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="message" className="kicker text-ash block mb-2">
          Votre message
        </label>
        <textarea
          id="message"
          rows={5}
          data-testid="contact-message"
          placeholder="Réclamation, question, demande de commande…"
          className={`${fieldClass} resize-none`}
          {...register("message", {
            required: "Merci d'écrire votre message.",
            minLength: { value: 10, message: "Votre message est un peu court." },
          })}
        />
        {errors.message && (
          <p data-testid="error-message" className="text-destructive text-xs mt-2">
            {errors.message.message}
          </p>
        )}
      </div>

      <button
        type="submit"
        data-testid="contact-submit"
        disabled={status === "sending"}
        className="inline-flex items-center justify-center gap-2 kicker bg-crust text-cream px-8 py-4 transition-all duration-200 active:scale-95 hover:bg-crustdark disabled:opacity-60 disabled:cursor-not-allowed w-full sm:w-auto"
      >
        {status === "sending" ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Envoi en cours…
          </>
        ) : (
          "Envoyer"
        )}
      </button>

      {/* Messages d'état */}
      {status === "success" && (
        <div
          data-testid="contact-success"
          role="status"
          className="flex items-start gap-3 bg-dough border border-crustborder p-4 text-charcoal text-sm"
        >
          <CheckCircle2 size={18} className="text-crust mt-0.5 shrink-0" />
          <span>{feedback}</span>
        </div>
      )}
      {status === "error" && (
        <div
          data-testid="contact-error"
          role="alert"
          className="flex items-start gap-3 bg-destructive/10 border border-destructive/30 p-4 text-charcoal text-sm"
        >
          <AlertCircle size={18} className="text-destructive mt-0.5 shrink-0" />
          <span>{feedback}</span>
        </div>
      )}
    </form>
  );
};
